/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { Box, TextareaAutosize, Typography, useTheme } from "@mui/material";
import React from "react";

import { Period } from "hooks/periodIndex";
import { useMessageFormActions } from "hooks/useMessageFormActions";
import { usePeriodNameFormatter } from "hooks/usePeriodNameFormatter";

import { PeriodSummary, PlanBackupSummary } from "src/store/api/generatedApi";
import { useAppSelector } from "src/store/store";
import { PlanId } from "components/common/boundary/PlanId";

interface Props {
  periods: PeriodSummary[];
  backups: PlanBackupSummary[];
  formPlanId: PlanId;
}

interface PeriodMessageProps {
  planId: PlanId;
  period: Period;
  periodName: string;
}

interface BackupMessageProps {
  formPlanId: PlanId;
  backupPlanId: PlanId;
  backupName: string;
  periodName: string;
}

interface TextareaProps {
  value: string | undefined;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

const Textarea = ({ value, onChange }: TextareaProps) => {
  return <TextareaAutosize value={value} onChange={onChange} maxRows={100} />;
};

const MessageBox = ({ children }: React.PropsWithChildren) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        textarea: {
          padding: 2,
          fontFamily: theme.typography.fontFamily,
          resize: "none",
          borderColor: theme.palette.grey[200],
          borderRadius: 1,
        },
      }}
    >
      {children}
    </Box>
  );
};

const PeriodMessage = ({ planId, period, periodName }: PeriodMessageProps) => {
  const { periodMessageSelector, setPeriodMessage } =
    useMessageFormActions(planId);
  const message = useAppSelector((state) =>
    periodMessageSelector(state, planId, period)
  );
  const handleOnChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPeriodMessage(planId, event.target.value, period);
  };
  return (
    <MessageBox>
      <Typography
        variant="h6"
        sx={{ display: "flex", flexWrap: "wrap", fontWeight: 900 }}
      >
        {periodName}
      </Typography>
      <Textarea value={message} onChange={handleOnChange} />
    </MessageBox>
  );
};

const BackupMessage = ({
  formPlanId,
  backupName,
  backupPlanId,
  periodName,
}: BackupMessageProps) => {
  const { backupMessageSelector, setBackupMessage } =
    useMessageFormActions(formPlanId);
  const message = useAppSelector((state) =>
    backupMessageSelector(state, formPlanId, backupPlanId)
  );
  const handleOnChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBackupMessage(formPlanId, event.target.value, backupPlanId);
  };
  return (
    <MessageBox>
      <Typography variant="h6" sx={{ display: "flex", flexWrap: "wrap" }}>
        {periodName}
        <ArrowRightAltIcon />
        <Typography
          variant="subtitle2"
          component="span"
          sx={{
            alignSelf: "flex-end",
            fontWeight: 900,
          }}
        >
          {backupName}
        </Typography>
      </Typography>
      <Textarea value={message} onChange={handleOnChange} />
    </MessageBox>
  );
};

export const MessageForm = ({ periods, backups, formPlanId }: Props) => {
  const { messagedPeriods, messagedBackupPlanIds } =
    useMessageFormActions(formPlanId);
  const periodNameFormatter = usePeriodNameFormatter();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {messagedPeriods !== undefined
        ? periods.map((period) => (
            <React.Fragment key={period.period}>
              {messagedPeriods.includes(period.period.toString()) ? (
                <PeriodMessage
                  planId={formPlanId}
                  period={period.period as Period}
                  periodName={periodNameFormatter(period.period as Period, {
                    startTimestamp: period.start_timestamp,
                    endTimestamp: period.end_timestamp,
                    name: period.name,
                  })}
                />
              ) : null}
              {period.period === 1
                ? backups
                    .filter(
                      (
                        backup: PlanBackupSummary
                      ): backup is Omit<PlanBackupSummary, "plan_id"> & {
                        plan_id: PlanId;
                      } => backup.plan_id !== null
                    )
                    .filter((backup) =>
                      messagedBackupPlanIds.includes(backup.plan_id.toString())
                    )
                    .map((backup) => (
                      <BackupMessage
                        key={backup.plan_id}
                        formPlanId={formPlanId}
                        backupName={backup.display_name}
                        backupPlanId={backup.plan_id}
                        periodName={periodNameFormatter(
                          period.period as Period,
                          {
                            startTimestamp: period.start_timestamp,
                            endTimestamp: period.end_timestamp,
                            name: period.name,
                          }
                        )}
                      />
                    ))
                : null}
            </React.Fragment>
          ))
        : null}
    </Box>
  );
};
